import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { PercentPipe } from '../../../pipes/percent.pipe';
import { AssetAllocation } from '../../../domain/lti/investment-portfolio.domain';
import { AssetCategoryType } from '../../../domain/lti/asset-allocation.domain';
import type Highcharts from 'highcharts';
export type DisplayField = 'targetAllocationPct' | 'currentAllocationPct' | 'targetAllocationAmt' | 'currentAllocationAmt';

@Injectable({
  providedIn: 'root'
})
export class AssetAllocationChartService {
  private readonly percentPipe = inject(PercentPipe);
  private readonly translateService = inject(TranslateService);

  private static readonly ASSET_CATEGORY_COLOR: Map<AssetCategoryType, string> = new Map<AssetCategoryType, string>([
    [AssetCategoryType.CANADIAN_EQUITIES, '#008a00'],
    [AssetCategoryType.US_EQUITIES, '#439d2f'],
    [AssetCategoryType.INTERNATIONAL_EQUITIES, '#719603'],
    [AssetCategoryType.CANADIAN_FIXED_INCOME, '#933214'],
    [AssetCategoryType.INTERNATIONAL_FIXED_INCOME, '#c47800'],
    [AssetCategoryType.CASH, '#ad842a'],
    [AssetCategoryType.OTHER, '#31728c']
  ]);

  createChartConfiguration(assetAllocations: Array<AssetAllocation>,
                           displayField: DisplayField,
                           size: number,
                           innerSize: number | string,
                           animation: boolean): Highcharts.Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    assetAllocations = this.filterAllocations(assetAllocations, displayField);
    const config: Highcharts.Options = {
      accessibility: {
        enabled: true
      },
      chart: {
        backgroundColor: '#ffffff',
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        type: 'pie',
        margin: [0, 0, 0, 0],
        spacingTop: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        spacingRight: 0,
        height: size,
        width: size
      },
      title: {
        text: ' '
      },
      tooltip: {
        outside: false,
        style: {
          position: 'relative !important',
          zIndex: 10001
        },
        formatter(): string {
          const args = {
            category: this.name,
            allocation: self.percentPipe.transform(this.y, { format: 'n0' })
          };
          return self.translateService.instant('tdWb.lti.assetAllocationChart.assetCategoryChart.TOOLTIP', args);
        }
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          dataLabels: {
            enabled: false
          },
          colors: this.getPieChartColors(assetAllocations),
          borderWidth: 3,
          borderRadius: 0
        },
        series: {
          states: {
            inactive: {
              opacity: 1
            },
            hover: {
              halo: {
                size: 0
              },
              enabled: false
            }
          }
        }
      },
      series: [{
        name: 'Asset Allocation',
        data: this.getSeriesDataPoints(assetAllocations, displayField),
        type: 'pie'
      }]
    };
    if (innerSize !== undefined) {
      config.plotOptions.pie.innerSize = innerSize;
    }
    if (animation === true) {
      config.plotOptions.series.animation = {
        duration: 1000
      };
    }
    if (assetAllocations.length === 1)  {
      config.plotOptions.pie.endAngle = 359.7; // to have gap when allocation 100%
    }

    return config;
  }

  getAssetCategoryColor(assetCategoryType: AssetCategoryType): string {
    return AssetAllocationChartService.ASSET_CATEGORY_COLOR.get(assetCategoryType);
  }

  filterAllocations(assetAllocations: Array<AssetAllocation>,
                    displayField: DisplayField): Array<AssetAllocation> {
    return assetAllocations
      .filter((allocation: AssetAllocation): boolean => allocation[displayField] > 0 )
      .slice();
  }

  private getSeriesDataPoints(assetAllocations: Array<AssetAllocation>,
                              displayField: DisplayField): Array<Highcharts.Point> {
    const dataPoints: Array<Highcharts.Point> = new Array<Highcharts.Point>();
    for (const allocation of assetAllocations) {
      dataPoints.push({
        name: this.translateService.instant('tdWb.lti.domain.assetCategoryType.' + allocation.assetCategoryType),
        y: allocation[displayField]
      } as Highcharts.Point);
    }
    return dataPoints;
  }

  private getPieChartColors(assetAllocations: Array<AssetAllocation>): Array<string> {
    const colors: Array<string> = new Array<string>();
    for (const allocation of assetAllocations) {
      colors.push(this.getAssetCategoryColor(allocation.assetCategoryType));
    }
    return colors;
  }
}

